import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

import { useSettings } from '../../context/SettingsContext';
import { API_URL } from '../Constants';
import Success from '../partials/Success';

const Validate = () => {
  const settings = useSettings();
  const navigate = useNavigate();
  const { id } = useParams(); // This is the transaction key from the QR code URL

  const [isValidating, setIsValidating] = useState(false);
  const [validated, setValidated] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [timeCreated, setTimeCreated] = useState('');
  const [missingSessionKey, setMissingSessionKey] = useState(false);
  const [missingTransactionKey, setMissingTransactionKey] = useState(false);

  useEffect(() => {
    // Check if transaction key is present in URL
    if (!id || id === 'new') {
      setMissingTransactionKey(true);
      return;
    }

    // Check if session key exists in localStorage
    const sessionKey = localStorage.getItem('validator_session_key');
    if (!sessionKey) {
      setMissingSessionKey(true);
      return;
    }

    // Both keys are present, proceed with validation
    validateParking(sessionKey, id);
  }, [id]);

  const validateParking = async (sessionKey, transactionKey) => {
    setIsValidating(true);
    setError('');

    try {
      // Create FormData object
      const formDataToSend = new FormData();
      formDataToSend.append('session_key', sessionKey);
      formDataToSend.append('transaction_key', transactionKey);

      const response = await axios.post(`${API_URL}validate`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setSuccessMessage(response.data.message);
        setCustomerName(response.data.customer_name || '');
        setTimeCreated(response.data.time_created || '');
        setValidated(true);
      } else {
        setError(response.data.error || 'An error occurred during validation.');
      }
    } catch (err) {
      if (err.response?.status === 401) {
        // Authentication error - clear session and redirect to login
        localStorage.removeItem('validator_session_key');
        localStorage.removeItem('validator_session_expires');
        setMissingSessionKey(true);
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('An error occurred during validation.');
      }
    } finally {
      setIsValidating(false);
    }
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '';
    
    try {
      const date = new Date(dateTimeString);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (err) {
      return dateTimeString;
    }
  };

  // Missing transaction key
  if (missingTransactionKey) {
    return (
      <div className='min-h-screen bg-gray-50'>
        <nav className="w-full fixed z-50">
          <div className="w-full mx-auto bg-primary lg:py-3 py-5">
            <h1 className='text-white text-center my-0'>Valparx Validation</h1>
          </div>
        </nav>

        <Success />
      </div>
    );
  }

  // Missing session key
  if (missingSessionKey) {
    return (
      <div className='min-h-screen bg-gray-50'>
        <nav className="w-full fixed z-50">
          <div className="w-full mx-auto bg-primary lg:py-3 py-5">
            <h1 className='text-white text-center my-0'>Valparx Validation</h1>
          </div>
        </nav>

        <div className='flex items-center justify-center min-h-screen pt-20'>
          <div className='max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center'>
            <div className='mb-6'>
              <svg 
                className='mx-auto h-16 w-16 text-red-500' 
                fill='none' 
                stroke='currentColor' 
                viewBox='0 0 24 24'
              >
                <path 
                  strokeLinecap='round' 
                  strokeLinejoin='round' 
                  strokeWidth={2} 
                  d='M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z' 
                />
              </svg>
            </div>
            <h2 className='text-2xl font-bold text-gray-900 mb-4'>
              Login Required
            </h2>
            <p className='text-gray-600 mb-8'>
              You need to login to validate parking. Please login with your validator account credentials.
            </p>
            <button
              onClick={() => navigate('/')}
              className='w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200'
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Validating state
  if (isValidating) {
    return (
      <div className='min-h-screen bg-gray-50'>
        <nav className="w-full fixed z-50">
          <div className="w-full mx-auto bg-primary lg:py-3 py-5">
            <h1 className='text-white text-center my-0'>Valparx Validation</h1>
          </div>
        </nav>

        <div className='flex items-center justify-center min-h-screen pt-20'>
          <div className='max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center'>
            <div className='mb-6'>
              <div className='inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600'></div>
            </div>
            <h2 className='text-2xl font-bold text-gray-900 mb-4'>
              Validating Parking...
            </h2>
            <p className='text-gray-600'>
              Please wait while we process the validation.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (validated) {
    return (
      <div className='min-h-screen bg-gray-50'>
        <nav className="w-full fixed z-50">
          <div className="w-full mx-auto bg-primary lg:py-3 py-5">
            <h1 className='text-white text-center my-0'>Valparx Validation</h1>
          </div>
        </nav>

        <div className='flex items-center justify-center min-h-screen pt-20'>
          <div className='max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center'>
            <div className='mb-6'>
              <svg 
                className='mx-auto h-16 w-16 text-green-500' 
                fill='none' 
                stroke='currentColor' 
                viewBox='0 0 24 24'
              >
                <path 
                  strokeLinecap='round' 
                  strokeLinejoin='round' 
                  strokeWidth={2} 
                  d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' 
                />
              </svg>
            </div>
            <h2 className='text-2xl font-bold text-green-600 mb-4'>
              Parking Validated!
            </h2>
            <div className='mb-6 p-4 bg-green-50 border border-green-200 rounded-lg'>
              <p className='text-gray-700 mb-4'>
                {successMessage}
              </p>
              {customerName && (
                <div className='text-left space-y-2 border-t border-green-200 pt-4'>
                  <div className='flex justify-between'>
                    <span className='font-semibold text-gray-700'>Customer:</span>
                    <span className='text-gray-900'>{customerName}</span>
                  </div>
                  {timeCreated && (
                    <div className='flex justify-between'>
                      <span className='font-semibold text-gray-700'>Entry Time:</span>
                      <span className='text-gray-900'>{formatDateTime(timeCreated)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            <button
              onClick={() => {
                setValidated(false);
                navigate('/validate/new');
              }}
              className='w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200'
            >
              Validate Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className='min-h-screen bg-gray-50'>
        <nav className="w-full fixed z-50">
          <div className="w-full mx-auto bg-primary lg:py-3 py-5">
            <h1 className='text-white text-center my-0'>Valparx Validation</h1>
          </div>
        </nav>

        <div className='flex items-center justify-center min-h-screen pt-20'>
          <div className='max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center'>
          <div className='mb-6'>
            <svg 
              className='mx-auto h-16 w-16 text-red-500' 
              fill='none' 
              stroke='currentColor' 
              viewBox='0 0 24 24'
            >
              <path 
                strokeLinecap='round' 
                strokeLinejoin='round' 
                strokeWidth={2} 
                d='M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z' 
              />
            </svg>
          </div>
          <h2 className='text-2xl font-bold text-red-600 mb-4'>
            Validation Error
          </h2>
          <div className='mb-6 p-4 bg-red-50 border border-red-200 rounded-lg'>
            <p className='text-gray-700 mb-2'>
              {error}
            </p>
            <p className='text-sm text-gray-600'>
              You will need to re-scan the QR code to try again.
            </p>
          </div>
          <button
            onClick={() => navigate('/validate/new')}
            className='w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 mb-3'
          >
            Try Again
          </button>
          <button
            onClick={() => navigate('/')}
            className='w-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3 px-6 rounded-lg transition duration-200'
          >
            Return to Home
          </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default Validate;
